import { useLocation } from 'react-router-dom';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import useFundAccess from '@/hooks/useFundAccess';
import useFeatureFlags from '@/contexts/FeatureFlags/useFeatureFlags';

/*
 * מה שסרגל הטאבים יציג, ואם בכלל (HAT-1170).
 *
 * ⚠️ בקובץ נפרד מהרכיב, ולא לצידו: קובץ שמייצא גם רכיב וגם הוק מבטל
 * את ה-Fast Refresh של Vite עבורו — כל שינוי בו מרענן את העמוד במקום
 * להחליף את הרכיב במקום.
 *
 * למה זה מיוצא בכלל: כשהטאבים מוצגים, הטאב הפעיל **הוא** הכותרת —
 * בדיוק כמו במקור (hatzdaka-nedarim), שאין בו כותרת לוח נפרדת —
 * וכותרת נוספת מתחתיו הייתה מדפיסה את אותה מילה פעמיים במסך שבו כל
 * שורה יקרה. כשהם לא מוצגים, הכותרת חייבת להישאר, אחרת לא נשאר שום
 * דבר שאומר לאיזה לוח הגעת. הלוח שואל, ולא מנחש.
 */

export interface TabDef {
  /** ה-route של האתר. גם הכתובת וגם המפתח לבדיקת ההרשאה. */
  to: string;
  /** מפתח התרגום של התווית. */
  labelKey: string;
  /** כשקיים — הדגל הוא הקובע הבלעדי, בדיוק כמו ב-NavItem. */
  featureKey?: string;
  /**
   * התווית כשהלוח הוא היחיד שנשאר בקבוצה שלו ומוצג כטאב ראשי. «אשראי» היא
   * קפסולה טובה ושם רע לטאב ראשי; לבד הוא נקרא כמו שנקרא תמיד —
   * «הוראות קבע אשראי».
   */
  soloLabelKey?: string;
}

/**
 * טאב ראשי שהוא קבוצה של לוחות (HAT-1135): «תמיכה», ומאז שיש מסך בנקאי גם
 * «הוראות קבע». הקבוצה מוצגת כטאב ראשי שמוביל ללוח הראשון שבתוכה, ומתחתיו
 * שורת קפסולות — בדיוק כמו קבוצה בסרגל הצד. קבוצה שנשאר בה לוח אחד מוצגת
 * כטאב רגיל, בלי קפסולות: קפסולה יחידה אינה בחירה.
 */
export interface TabGroupDef {
  labelKey: string;
  tabs: TabDef[];
}

/** קבוצה אחרי סינון ההרשאות, כפי שהרכיב מצייר אותה. */
export interface TabGroup {
  labelKey: string;
  tabs: TabDef[];
  /** הלוח שהטאב הראשי מוביל אליו. */
  first: TabDef;
  isOn: boolean;
}

/*
 * ⚠️ הרשימות כאן **אינן** רשימה שנייה של מסכים. הן נגזרות מאותם שני
 * שערים שסרגל הצד נשען עליהם — VITE_APP_ENABLED_ROUTES ודגלי התכונות —
 * כדי שלא ייווצר מצב שבו לוח מוסתר בסרגל הצד ופתוח כאן.
 */
const enabledRoutes: string[] = import.meta.env.VITE_APP_ENABLED_ROUTES ?? [];

/** הטאבים הראשיים, בסדר של המקור: לוח תנועות, הוראות קבע, תמיכה. */
const MAIN_TABS: (TabDef | TabGroupDef)[] = [
  { to: 'transactions', labelKey: 'transactionsBoard' },
  {
    labelKey: 'standingOrdersGroup',
    tabs: [
      { to: 'standing-orders', labelKey: 'standingOrdersCredit', featureKey: 'standing-orders', soloLabelKey: 'debits' },
      { to: 'bank-standing-orders', labelKey: 'standingOrdersBank', featureKey: 'bank-standing-orders' },
    ],
  },
  {
    labelKey: 'support',
    tabs: [
      { to: 'support/support-requests', labelKey: 'supportRequests' },
      { to: 'support/supporteds', labelKey: 'supporteds' },
      { to: 'support/goals', labelKey: 'supportGoals' },
      { to: 'support/inquiries', labelKey: 'correspondences', featureKey: 'inquiries' },
    ],
  },
];

const isGroup = (entry: TabDef | TabGroupDef): entry is TabGroupDef => 'tabs' in entry;

export const useBoardTabs = () => {
  const { isMobile } = useDeviceDetection();
  const { isEnabled } = useFeatureFlags();
  const location = useLocation();
  // Permission worlds (Druk, 22.10.2026): no tab into a world closed to this coordinator.
  const { canOpen } = useFundAccess();

  const isTabAllowed = (tab: TabDef) =>
    (tab.featureKey ? isEnabled(tab.featureKey) : enabledRoutes.includes(tab.to)) && canOpen(tab.to);

  // הכתובת נושאת את מזהה הקרן ("/new/2/transactions"), ולכן ההשוואה
  // היא על הסיומת ולא על הנתיב המלא.
  const isOn = (to: string) => location.pathname.endsWith(`/${to}`);

  /*
   * ⚠️ הקישורים נבנים כנתיב **מוחלט** מבסיס הקרן, ולא יחסית לכתובת
   * הנוכחית. הגרסה הקודמת השתמשה ב-`../<to>` עם relative="path" — וזה
   * נכון רק כשעומדים על נתיב בעומק אחד ("/new/2/transactions"). מתוך
   * "/new/2/support/support-requests" ה-".." עולה ל-"/new/2/support",
   * וקפסולת "נתמכים" יצאה "/new/2/support/support/supporteds" — נתיב
   * שאין לו route, כלומר דף לבן. זה הבאג "כפתורים שמביאים דף לבן"
   * שהמשתמשת דיווחה עליו.
   */
  /*
   * ⚠️ useLocation מחזיר נתיב **יחסי ל-basename של הראוטר** ("/new"):
   * מתוך "/new/2/transactions" מתקבל "/2/transactions". לכן הבסיס הוא
   * המקטע הראשון — מזהה הקרן — ולא "/new/<id>"; ה-"/new" מתווסף
   * מעצמו על ידי NavLink. חיפוש "/new/" כאן נכשל בשקט והחזיר בסיס ריק,
   * וכל הקישורים איבדו את הקרן.
   */
  const fundBase = location.pathname.match(/^\/[^/]+/)?.[0] ?? '';
  const hrefOf = (to: string) => `${fundBase}/${to}`;

  /*
   * מה נשאר אחרי ההרשאות: טאב בודד נשאר טאב; קבוצה עם לוח אחד מתקפלת
   * לטאב שמוביל אליו ונושא את שם הקבוצה (כמו «הוראות קבע» כשרק האשראי
   * פתוח); קבוצה עם יותר מלוח אחד מקבלת קפסולות.
   */
  const entries: (TabDef | TabGroup)[] = [];
  for (const entry of MAIN_TABS) {
    if (!isGroup(entry)) {
      if (isTabAllowed(entry)) entries.push(entry);
      continue;
    }
    const tabs = entry.tabs.filter(isTabAllowed);
    if (tabs.length === 0) continue;
    if (tabs.length === 1) {
      entries.push({ to: tabs[0].to, labelKey: tabs[0].soloLabelKey ?? entry.labelKey, featureKey: tabs[0].featureKey });
      continue;
    }
    entries.push({ labelKey: entry.labelKey, tabs, first: tabs[0], isOn: tabs.some((tab) => isOn(tab.to)) });
  }

  const activeGroup = entries.find((entry): entry is TabGroup => 'tabs' in entry && entry.isOn) ?? null;

  /*
   * מעל 1100 סרגל הצד הוא הניווט, והלוח חוזר לטבלה. וקבוצה אחת בלבד
   * אינה ניווט — היא רק תופסת גובה.
   */
  const isVisible = isMobile && entries.length >= 2;

  return {
    isVisible,
    /** טאבים וקבוצות, בסדר ההצגה. */
    entries,
    /** הקבוצה שהלוח הנוכחי שייך אליה, אם יש — הקפסולות שמתחת לשורה. */
    activeGroup,
    isOn,
    hrefOf,
  };
};

export const isTabGroup = (entry: TabDef | TabGroup): entry is TabGroup => 'tabs' in entry;

export default useBoardTabs;
